import * as React from 'react';
import { Component } from 'react';
import styles from '../../styles/flyouts/working_set_specifications/Notes.scss';
import * as _ from 'lodash';
import classNames from 'classnames';
import autobind from 'autobind-decorator';
import {
  __,
  getLocalized,
  mapStateToProps,
  setTableParams,
  validateForm,
} from '../../core/utils';
import UncheckedIcon from '../../assets/images/unchecked-15x15.svg';
import NeutralIcon from '../../assets/images/neutral-15x15.svg';
import CheckedIcon from '../../assets/images/checked-15x15.svg';
import CloseMediumIcon from '../../assets/images/close-middle-15x15.svg';
import DeleteIcon from '../../assets/images/delete-15x15.svg';
import EditIcon from '../../assets/images/edit-16x16.svg';
import { ReactSortable } from 'react-sortablejs';
import GrabberIcon from '../../assets/images/grabber-16x16.svg';
import { setNotification } from '../../redux/actions/general/notification';
import {
  hideAlertbox,
  showAlertbox,
} from '../../redux/actions/general/alertbox';
import Tooltip from '../../components/Tooltip';
import {
  deleteSpecificationProductNote,
  reorderSpecificationProductNotes,
  updateSpecificationProjectNote,
} from '../../redux/actions/table/working_set_specifications';
import ProjectNoteForm from '../../forms/working_set_specifications/ProjectNoteForm';
import ArrowDownMiddleIcon from '../../assets/images/arrow-down-middle-15x15.svg';
import LogoIcon from '../../assets/images/logo-50x50.svg';

@mapStateToProps((state) => ({
  store: state.table.working_set_specifications,
}))
class Notes extends Component {
  constructor(props) {
    super(props);

    this.state = {
      categoryOrder: [],
      expanded: [],
      selectedNoteIds: [],
    };

    this.pendingNoteOrder = null;
  }

  _getCategoryOrder(notes, categoryOrder = []) {
    const categoryIds = _.sortBy(_.uniq(_.map(notes, 'category_id')));

    return [
      ..._.intersection(categoryOrder, categoryIds),
      ..._.difference(categoryIds, categoryOrder),
    ];
  }

  @autobind
  _changeNoteOrder(category, notes) {
    const { flyout } = this.props.store;
    const categoryNotes = _.sortBy(
      _.filter(flyout.notes, ['category_id', category]),
      'order'
    );
    const categoryOrderSlots = _.map(categoryNotes, 'order');
    const updatedNotes = _.map(flyout.notes, (note) => {
      if (note.category_id != category) {
        return note;
      }

      return {
        ...note,
        order: _.get(
          categoryOrderSlots,
          _.findIndex(notes, ['id', note.id]),
          note.order
        ),
      };
    });

    this.pendingNoteOrder = _.map(_.sortBy(updatedNotes, 'order'), 'id');

    setTableParams('working_set_specifications', {
      flyout: {
        ...flyout,
        notes: updatedNotes,
      },
    });
  }

  @autobind
  _changeNoteCategoryOrder(categories) {
    const { flyout } = this.props.store;
    const updatedNotes = _.map(
      _.transform(
        categories,
        (result, category) => {
          _.each(_.sortBy(flyout.notes, 'order'), (note) => {
            if (note.category_id == category.value) {
              result.push(note);
            }
          });
        },
        []
      ),
      (note, i) => ({
        ...note,
        order: i,
      })
    );

    this.pendingNoteOrder = _.map(updatedNotes, 'id');
    this.setState({
      categoryOrder: _.map(categories, 'value'),
    });

    setTableParams('working_set_specifications', {
      flyout: {
        ...flyout,
        notes: updatedNotes,
      },
    });
  }

  @autobind
  _toggleCollapseCategory(category) {
    this.setState({
      expanded: _.xor(this.state.expanded, [category]),
    });
  }

  @autobind
  _toggleNoteSelection(note_id) {
    this.setState((prevState) => ({
      selectedNoteIds: _.xor(prevState.selectedNoteIds, [note_id]),
    }));
  }

  @autobind
  _toggleAllNotes() {
    const notes = _.get(this.props, 'store.flyout.notes', []);

    if (_.isEmpty(notes)) {
      return;
    }

    this.setState((prevState) => {
      const noteIds = _.map(notes, 'id');

      return {
        selectedNoteIds: _.isEmpty(
          _.difference(noteIds, prevState.selectedNoteIds)
        )
          ? []
          : noteIds,
      };
    });
  }

  @autobind
  _deleteNotes(noteIds) {
    const { flyout } = this.props.store;
    const existingNoteIds = _.intersection(
      _.uniq(_.castArray(noteIds)),
      _.map(_.get(flyout, 'notes', []), 'id')
    );

    if (_.isEmpty(existingNoteIds)) {
      return;
    }

    showAlertbox({
      color: 'red',
      title: __('general.alert.are-you-sure'),
      description: __(
        'working-set-specifications.instructions.are-you-sure-remove-this-clause'
      ),
      buttons: [
        {
          color: 'lightGray',
          text: __('general.alert.no-close'),
          onClick: 'close',
        },
        {
          color: 'gray',
          text: __('general.alert.yes-delete'),
          onClick: () =>
            deleteSpecificationProductNote(flyout.id, existingNoteIds).then(
              () => {
                hideAlertbox();

                setNotification(
                  __(
                    'working-set-specifications.instructions.selected-instructions-deleted'
                  )
                );

                setTableParams('working_set_specifications', {
                  flyout: {
                    ...flyout,
                    notes: _.reject(flyout.notes, (note) =>
                      _.includes(existingNoteIds, note.id)
                    ),
                  },
                });

                this.setState((prevState) => ({
                  selectedNoteIds: _.difference(
                    prevState.selectedNoteIds,
                    existingNoteIds
                  ),
                }));
              }
            ),
        },
      ],
    });
  }

  @autobind
  _handleSubmit(formData) {
    return validateForm(
      updateSpecificationProjectNote(this.props.store.flyout.id, formData).then(
        () => {
          setNotification(
            __(
              'working-set-specifications-flyout.specification-project-note-updated'
            )
          );

          this.props._refetchData();
        }
      )
    );
  }

  componentDidMount() {
    const { notes } = this.props.store.flyout;

    this.setState({
      categoryOrder: this._getCategoryOrder(notes),
      expanded: _.map(_.groupBy(notes, 'category_id'), (data, category_id) => {
        return Number(category_id);
      }),
    });
  }

  componentDidUpdate(prevProps) {
    const { notes } = this.props.store.flyout;
    const prevNoteIds = _.map(_.get(prevProps, 'store.flyout.notes', []), 'id');
    const prevCategoryIds = _.map(
      _.get(prevProps, 'store.flyout.notes', []),
      'category_id'
    );
    const noteIds = _.map(notes, 'id');
    const categoryIds = _.map(notes, 'category_id');

    if (
      // _.get(prevProps, 'store.flyout.notes') &&
      // _.get(this.props, 'store.flyout.notes') &&
      !_.isEqual(prevNoteIds, noteIds) ||
      !_.isEqual(prevCategoryIds, categoryIds)
    ) {
      this.setState((prevState) => ({
        categoryOrder: this._getCategoryOrder(notes, prevState.categoryOrder),
        expanded: _.map(
          _.groupBy(notes, 'category_id'),
          (data, category_id) => {
            return Number(category_id);
          }
        ),
        selectedNoteIds: _.intersection(prevState.selectedNoteIds, noteIds),
      }));
    }
  }

  render() {
    const { collapsed, store, readOnly } = this.props;
    const { flyout, language_id } = store;
    const { expanded, selectedNoteIds } = this.state;

    let notes = _.sortBy(flyout.notes, 'order');
    const noteIds = _.map(notes, 'id');
    const categoryOrder = this._getCategoryOrder(
      notes,
      this.state.categoryOrder
    );
    const allNotesSelected =
      !_.isEmpty(notes) && _.isEmpty(_.difference(noteIds, selectedNoteIds));

    let note_categories = _.sortBy(
      _.transform(
        notes,
        (result, note) => {
          if (!result.find((data) => data.value == note.category_id)) {
            result.push({
              label: getLocalized(note.category_name, language_id),
              value: note.category_id,
              order: _.indexOf(categoryOrder, note.category_id),
            });
          }
        },
        []
      ),
      'order'
    );

    return (
      <div
        className={classNames(styles.wrapper, collapsed && styles.collapsed)}
      >
        <ProjectNoteForm
          readOnly={readOnly}
          onSubmit={this._handleSubmit}
          initialValues={{
            project_note: flyout.project_note || {},
          }}
        />

        {!readOnly && !_.isEmpty(notes) && (
          <div
            className={classNames(
              styles.bulkActions,
              !_.isEmpty(selectedNoteIds) && styles.visible
            )}
          >
            <Tooltip
              text={__(
                'working-set-specifications-flyout.instructions.delete-selected-instructions'
              )}
            >
              <DeleteIcon
                className={styles.bulkDelete}
                onClick={() => this._deleteNotes(selectedNoteIds)}
              />
            </Tooltip>
          </div>
        )}

        <ReactSortable
          list={note_categories}
          setList={this._changeNoteCategoryOrder}
          animation={200}
          disabled={readOnly}
          delayOnTouchStart={true}
          onEnd={() => {
            reorderSpecificationProductNotes(
              flyout.id,
              this.pendingNoteOrder ||
                _.map(_.sortBy(flyout.notes, 'order'), 'id')
            ).then(() => {
              this.pendingNoteOrder = null;

              setNotification(
                __('product-attributes-group-order.saved.notification')
              );
            });
          }}
          handle={'.' + styles.grabber}
        >
          {_.map(note_categories, (category) => {
            const filtered_notes = _.sortBy(
              _.filter(notes, ['category_id', category.value]),
              'order'
            );

            return (
              <div className={styles.category} key={category.value}>
                <div
                  className={styles.title}
                  onClick={() => this._toggleCollapseCategory(category.value)}
                >
                  <div className={styles.grabber}>
                    <GrabberIcon />
                  </div>
                  <ArrowDownMiddleIcon
                    className={classNames(
                      styles.collapse,
                      !_.includes(expanded, category.value) && styles.collapsed
                    )}
                  />
                  {category.label}
                </div>
                <ReactSortable
                  disabled={readOnly}
                  list={filtered_notes}
                  setList={(list) =>
                    this._changeNoteOrder(category.value, list)
                  }
                  animation={200}
                  delayOnTouchStart={true}
                  onEnd={() => {
                    reorderSpecificationProductNotes(
                      flyout.id,
                      this.pendingNoteOrder || _.map(notes, 'id')
                    ).then(() => {
                      this.pendingNoteOrder = null;

                      setNotification(
                        __(
                          'working-set-specifications.instructions.specification-instructions-order-saved'
                        )
                      );
                    });
                  }}
                  handle={'.' + styles.grabber}
                  className={styles.content}
                >
                  {_.includes(expanded, category.value) &&
                    _.map(filtered_notes, (note) => {
                      const isSelected = _.includes(selectedNoteIds, note.id);

                      return (
                        <div
                          className={classNames(styles.flex, styles.row)}
                          key={note.id}
                        >
                          <div className={styles.grabber}>
                            <GrabberIcon />
                          </div>
                          {!readOnly && (
                            <div
                              className={classNames(
                                styles.checkbox,
                                isSelected && styles.selected
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                this._toggleNoteSelection(note.id);
                              }}
                            >
                              {isSelected ? (
                                <CheckedIcon className={styles.checked} />
                              ) : (
                                <UncheckedIcon className={styles.unchecked} />
                              )}
                            </div>
                          )}
                          <div className={styles.note}>
                            <span>&mdash;</span>

                            <span>{getLocalized(note.text, language_id)}</span>
                          </div>

                          <div className={styles.action}>
                            <div
                              className={classNames(
                                styles.bubble,
                                note.company_id ? styles.company : styles.system
                              )}
                            >
                              {note.company_id ? (
                                <Tooltip
                                  text={__(
                                    'specifications.current-set.flyout.notes.my-company-note'
                                  )}
                                >
                                  MY
                                </Tooltip>
                              ) : (
                                <Tooltip
                                  text={__(
                                    'specifications.current-set.flyout.notes.system-note'
                                  )}
                                >
                                  <LogoIcon />
                                </Tooltip>
                              )}
                            </div>

                            {(note.company_id || !readOnly) && (
                              <div className={styles.edit}>
                                <EditIcon
                                  onClick={() =>
                                    setTableParams(
                                      'working_set_specifications',
                                      {
                                        edit_specification_product_note_wizard: true,
                                        edit_specification_product_note_data:
                                          note,
                                      }
                                    )
                                  }
                                />
                              </div>
                            )}

                            {!readOnly && (
                              <div className={styles.remove}>
                                <CloseMediumIcon
                                  onClick={() => this._deleteNotes([note.id])}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </ReactSortable>
              </div>
            );
          })}
        </ReactSortable>

        {!readOnly && !flyout?.is_shared && (
          <div
            className={classNames(
              styles.addRow,
              _.size(notes) == 0 && styles.left
            )}
          >
            <button
              type='button'
              onClick={() =>
                setTableParams('working_set_specifications', {
                  add_specification_product_note_wizard: true,
                })
              }
            >
              {__('button.add-clause')}
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default Notes;
